import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useT } from "../i18n";

// Command palette (Ctrl/Cmd+K, UX phase 4). Generic: the parent supplies a flat Command[] (items to
// connect/open, actions, and navigation) and this component handles fuzzy filtering, keyboard
// navigation, and running. Built on the same data the vault already holds — no extra fetches.
export interface Command {
  id: string;
  group: string; // shown as a muted tag on the row (e.g. "Item", "Action", "Go to")
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  keywords?: string; // extra searchable text (host/username/tags) not shown
  run: () => void;
}

const MAX_RESULTS = 60;

// Subsequence fuzzy score (higher = better) or null when the query doesn't match at all.
function fuzzy(q: string, text: string): number | null {
  if (!q) return 0;
  const t = text.toLowerCase();
  const sub = t.indexOf(q);
  if (sub >= 0) return 1000 - sub; // contiguous substring; earlier is better
  let ti = 0;
  let gaps = 0;
  for (const ch of q) {
    const found = t.indexOf(ch, ti);
    if (found < 0) return null;
    gaps += found - ti;
    ti = found + 1;
  }
  return 200 - gaps;
}

export function CommandPalette({ getCommands, onClose }: { getCommands: () => Command[]; onClose: () => void }) {
  const t = useT();
  // Snapshot the command list once when the palette opens (the builder closes over refs, so it must
  // not run during the parent's render — taking it as a thunk and calling it here keeps that off).
  const [commands] = useState(getCommands);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands.slice(0, MAX_RESULTS);
    return commands
      .map((c) => {
        const inTitle = fuzzy(q, c.title);
        const score = inTitle != null ? inTitle + 50 : fuzzy(q, `${c.title} ${c.subtitle ?? ""} ${c.keywords ?? ""}`);
        return score == null ? null : { c, score };
      })
      .filter((x): x is { c: Command; score: number } => x !== null)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_RESULTS)
      .map((x) => x.c);
  }, [commands, query]);

  // Derived (never stored past the result set) so a shrinking list can't leave the cursor stranded.
  const activeIdx = Math.min(active, Math.max(0, results.length - 1));

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Keep the active row in view as it moves.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(Math.min(activeIdx + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(Math.max(activeIdx - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = results[activeIdx];
      if (cmd) {
        onClose();
        cmd.run();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-[12vh]" onClick={onClose}>
      <div
        className="flex max-h-[70vh] w-[640px] max-w-[92vw] flex-col overflow-hidden rounded-lg border border-border bg-panel shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={t("Type a command or search…")}
          className="border-b border-border bg-transparent px-4 py-3 text-sm outline-none"
        />
        <div ref={listRef} className="min-h-0 flex-1 overflow-auto p-1">
          {results.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted">{t("No results")}</div>}
          {results.map((c, i) => (
            <div
              key={c.id}
              data-idx={i}
              onMouseMove={() => setActive(i)}
              onClick={() => {
                onClose();
                c.run();
              }}
              className={`flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm ${i === activeIdx ? "bg-accent/20" : ""}`}
            >
              <span className="grid h-4 w-4 shrink-0 place-items-center text-muted">{c.icon ?? "•"}</span>
              <span className="min-w-0 flex-1 truncate">
                {c.title}
                {c.subtitle && <span className="ml-2 text-xs text-muted">{c.subtitle}</span>}
              </span>
              <span className="shrink-0 rounded bg-panel2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted">{c.group}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 border-t border-border px-3 py-1.5 text-[11px] text-muted">
          <span>↑↓ {t("navigate")}</span>
          <span>↵ {t("run")}</span>
          <span>esc {t("close")}</span>
          <span className="ml-auto">{results.length}</span>
        </div>
      </div>
    </div>
  );
}
